import { FieldValues, useFormContext, UseFormReturn } from 'react-hook-form';
import { Button } from '../Inputs/Button';
import styles from './HubspotFormWrapper.module.css';
import { Input } from '../Inputs/Input';
import { Textarea } from '../Inputs/Textarea';
import { Select } from '../Inputs/Select';
import { Text } from '../Text';

export const HubspotFormWrapper = props => {
	const formContext = useFormContext();
	const {
		reset,
		handleSubmit,
		formState: { isSubmitSuccessful, errors },
	} = formContext;

	const onSubmit = handleSubmit(async data => {
		await fetch(`/api/hubspot/forms?formId=${props.form.id}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		});

		reset();
	});

	if (isSubmitSuccessful) {
		return (
			<div className={styles.text}>
				<Text as='span' styleType='heading-sm'>
					{props.successHeading ?? 'Thank you!'}
				</Text>
				<span className={styles.description}>{props.successMessage ?? 'We will get back to you as soon as possible.'}</span>
			</div>
		);
	}

	return (
		// eslint-disable-next-line @typescript-eslint/no-misused-promises
		<form className={styles.form} method='POST' onSubmit={onSubmit} aria-hidden={isSubmitSuccessful}>
			{props.form.fieldGroups.map((group, index) => (
				<FieldGroupSerializer key={index} group={group} formContext={formContext} />
			))}

			{props.form.legalConsentOptions.privacyText && (
				<div className={`body-10 ${styles.legal}`} dangerouslySetInnerHTML={{ __html: props.form.legalConsentOptions.privacyText }} />
			)}

			<Button
				className='self-start'
				parent={props.form.name}
				styleSize='large'
				type='submit'
				tabIndex={isSubmitSuccessful ? -1 : 0}
				disabled={isSubmitSuccessful}
			>
				{props.buttonText}
			</Button>
		</form>
	);
};

const FieldGroupSerializer = props => {
	return (
		<div className={styles.row}>
			{props.group.fields?.map((field, index) => (
				<FieldSerializer key={index} field={field} formContext={props.formContext} />
			))}
		</div>
	);
};

const FieldSerializer = props => {
	const {
		register,
		watch,
		formState: { errors },
	} = props.formContext;

	const field = props.field;
	const watchAllFields = watch();
	const error = errors[field.name];

	switch (field.fieldType) {
		case 'single_line_text':
			return (
				<div className='form-item'>
					<Input
						id={field.name}
						label={field.placeholder ?? field.label}
						placeholder={field.placeholder ?? field.label}
						type='text'
						hasError={errors[field.name]?.type == 'required'}
						hasValue={!!watchAllFields[field.name]}
						//tabIndex={isSubmitSuccessful ? -1 : 0}
						autoComplete='firstName'
						{...register(field.name, {
							required: field.required,
							//disabled: isSubmitSuccessful,
						})}
					/>
				</div>
			);
		case 'email':
			return (
				<div className='form-item'>
					<Input
						id={field.name}
						label={field.placeholder ?? field.label}
						placeholder={field.placeholder ?? field.label}
						type='email'
						hasError={!!(errors.email && (errors.email?.type == 'required' || errors.email?.message !== ''))}
						hasValue={!!watchAllFields[field.name]}
						//tabIndex={isSubmitSuccessful ? -1 : 0}
						autoComplete='email'
						{...register(field.name, {
							required: field.required,
							//disabled: isSubmitSuccessful,
							pattern: {
								value:
									/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
								message: 'My valid email is',
							},
						})}
					/>
				</div>
			);
		case 'number':
			return (
				<div className='form-item'>
					<Input
						id={field.name}
						label={field.placeholder ?? field.label}
						placeholder={field.placeholder ?? field.label}
						type='number'
						hasError={errors[field.name]?.type == 'required'}
						hasValue={!!watchAllFields[field.name]}
						//tabIndex={isSubmitSuccessful ? -1 : 0}
						autoComplete='firstName'
						{...register(field.name, {
							required: field.required,
							//disabled: isSubmitSuccessful,
						})}
					/>
				</div>
			);
		case 'dropdown':
			return (
				<div className='form-item'>
					<Select
						id={field.name}
						label={field.placeholder ?? field.label}
						options={field.options.map(option => ({
							displayName: option.label,
							value: option.value,
						}))}
						hasError={errors[field.name]?.type == 'required'}
						hasValue={!!watchAllFields[field.name]}
						//tabIndex={isSubmitSuccessful ? -1 : 0}
						{...register(field.name, {
							required: field.required,
							//disabled: isSubmitSuccessful,
						})}
					/>
				</div>
			);
		case 'multi_line_text':
			return (
				<div className='form-item'>
					<Textarea
						id={field.name}
						label={field.placeholder ?? field.label}
						placeholder={field.placeholder ?? field.label}
						hasError={errors[field.name]?.type == 'required'}
						hasValue={!!watchAllFields[field.name]}
						//tabIndex={isSubmitSuccessful ? -1 : 0}
						rows={6}
						{...register('ticketDescription', {
							required: field.required,
							//disabled: isSubmitSuccessful,
						})}
					/>
				</div>
			);
		case 'phone':
		case 'mobile_phone':
			return (
				<div className='form-item'>
					<Input
						id={field.name}
						label={field.placeholder ?? field.label}
						placeholder={field.placeholder ?? field.label}
						type='tel'
						hasError={!!(errors[field.name] && (errors[field.name]?.type == 'required' || errors[field.name]?.message !== ''))}
						hasValue={!!watchAllFields[field.name]}
						//tabIndex={isSubmitSuccessful ? -1 : 0}
						autoComplete='tel'
						{...register(field.name, {
							required: true,
							//disabled: isSubmitSuccessful,
							pattern: {
								value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
								message: 'My valid phone number is',
							},
						})}
					/>
				</div>
			);
		default:
			return null;
	}
};
